import * as React from 'react'

import {
  HorizontalAlignment,
  i18n,
  Routes,
} from '@thg-commerce/enterprise-core'
import { EnableSignUp } from '@thg-commerce/enterprise-widgets/src/WaitlistSignupWidgetRenderer/WaitlistSignupWidgetRenderer'
import { GridItem } from '@thg-commerce/gravity-system'

import { StyledGrid, SubmitButton, Subtitle, Title } from './styles'
import { WaitlistSignupForm } from './WaitlistSignupForm'

export enum FormStatus {
  UNSUBMITTED = 'UNSUBMITTED',
  FAILURE = 'FAILURE',
  SUCCESSFUL = 'SUCCESSFUL',
  DUPLICATE = 'DUPLICATE',
}

export interface WaitlistSignupProps {
  title?: {
    text?: string
    horizontalAlignment?: HorizontalAlignment
  }
  subtitle?: {
    text?: string
    horizontalAlignment?: HorizontalAlignment
  }
  buttonText?: string
  campaignCode: string
  gdprDisclaimerText?: string
  emailPlaceholderText?: string
  fullNamePlaceholderText?: string
  enableSignUp?: EnableSignUp
  signUpCheckboxLabel?: string
  noAccountReturnUrl?: string
  registeredReturnUrl?: string
  errorReturnUrl?: string
}

export const WaitlistSignup = (props: WaitlistSignupProps) => {
  const [formStatus, setFormStatus] = React.useState<FormStatus>(
    FormStatus.UNSUBMITTED,
  )

  const [redirectHandled, setRedirectHandled] = React.useState<boolean>(false)

  const i18nText = {
    successTitle:
      i18n('general.waitlist.signup.success.title') ||
      'Thanks, we’ll be in touch',
    successSubtitle:
      i18n('general.waitlist.signup.success.subtitle') ||
      'Get ready for an exciting email landing in your inbox soon...',
    continueShoppingButtonText: i18n('general.continueshopping.label'),
    errorTitle:
      i18n('general.waitlist.signup.error.title') ||
      'Oops, something went wrong!',
    errorSubtitle:
      i18n('general.waitlist.signup.error.subtitle') ||
      'Click the below link to try again and don’t forget to select “I’m not a robot”, so you don’t miss out on this exciting launch.',
    errorButtonText:
      i18n('general.waitlist.signup.error.button.text') ||
      'Go back and try again',
  }

  React.useEffect(() => {
    if (formStatus === FormStatus.SUCCESSFUL) {
      if (props.noAccountReturnUrl && props.noAccountReturnUrl.trim() !== '') {
        window.location.href = props.noAccountReturnUrl
        setRedirectHandled(true)
        return
      }
    } else if (formStatus === FormStatus.FAILURE) {
      if (props.errorReturnUrl && props.errorReturnUrl.trim() !== '') {
        window.location.href = props.errorReturnUrl
        setRedirectHandled(true)
        return
      }
    } else if (formStatus === FormStatus.DUPLICATE) {
      if (props.noAccountReturnUrl && props.noAccountReturnUrl.trim() !== '') {
        window.location.href = props.noAccountReturnUrl
        setRedirectHandled(true)
        return
      }
    }
  }, [
    formStatus,
    props.noAccountReturnUrl,
    props.errorReturnUrl,
    props.registeredReturnUrl,
    redirectHandled,
  ])

  if (redirectHandled) {
    return null
  }
  return (
    <StyledGrid columns={12}>
      <GridItem colStart={[1, 4, 4, 4]} colSpan={[12, 6, 6, 6]}>
        {formStatus === FormStatus.UNSUBMITTED ? (
          <WaitlistSignupForm
            key="waitlist-form"
            title={props.title}
            subtitle={props.subtitle}
            buttonText={props.buttonText}
            gdprDisclaimerText={props.gdprDisclaimerText}
            emailPlaceholderText={props.emailPlaceholderText}
            fullNamePlaceholderText={props.fullNamePlaceholderText}
            campaignCode={props.campaignCode}
            setFormStatus={setFormStatus}
            enableSignUp={props.enableSignUp}
            signUpCheckboxLabel={props.signUpCheckboxLabel}
          />
        ) : (
          <React.Fragment>
            {formStatus === FormStatus.SUCCESSFUL ||
            formStatus === FormStatus.DUPLICATE ? (
              <React.Fragment key="success-container">
                <Title
                  data-testid="success-title"
                  horizontalAlignment={props.title?.horizontalAlignment}
                >
                  {i18nText.successTitle}
                </Title>
                <Subtitle
                  horizontalAlignment={props.subtitle?.horizontalAlignment}
                >
                  {i18nText.successSubtitle}
                </Subtitle>
                <SubmitButton
                  emphasis="high"
                  renderedAs="a"
                  href={Routes.HomePage}
                >
                  {i18nText.continueShoppingButtonText}
                </SubmitButton>
              </React.Fragment>
            ) : (
              <React.Fragment key="error-container">
                <Title
                  data-testid="error-title"
                  horizontalAlignment={props.title?.horizontalAlignment}
                >
                  {i18nText.errorTitle}
                </Title>
                <Subtitle
                  horizontalAlignment={props.subtitle?.horizontalAlignment}
                >
                  {i18nText.errorSubtitle}
                </Subtitle>
                <SubmitButton
                  data-testid="go-back-button"
                  emphasis="high"
                  onClick={() => {
                    setFormStatus(FormStatus.UNSUBMITTED)
                  }}
                >
                  {i18nText.errorButtonText}
                </SubmitButton>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </GridItem>
    </StyledGrid>
  )
}
